import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { updatePasswordData } from "@actions/updatePasswordAction";
import { useAppConfig } from "@context/AppConfigContext";

// Password validation rules - Simplified
const PASSWORD_RULES = {
  minLength: 5,
  maxLength: 50,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecialChar: true,
};

export const handleUpdatePassword = async (
  newPassword,
  currentPassword,
  email,
) => {
  try {
    const updatePasswordPayload = {
      newPassword: newPassword,
      email: email,
      currentPassword: currentPassword,
    };

    // Call API to update password
    const response = await updatePasswordData(updatePasswordPayload);

    // Handle successful response
    if (response.success) {
      toast.success(response.message);
      return response;
    } else {
      throw new Error(response.message || "Failed to update password");
    }
  } catch (error) {
    throw error;
  }
};

export const useChangePasswordData = () => {
  // Get user from AppConfig context
  const { user } = useAppConfig();

  // Form state
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  // Password visibility state
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate password strength - Simplified
  const validatePassword = (password) => {
    const errors = [];

    if (password.length < PASSWORD_RULES.minLength) {
      errors.push(
        `Password must be at least ${PASSWORD_RULES.minLength} characters long`,
      );
    }

    if (password.length > PASSWORD_RULES.maxLength) {
      errors.push(
        `Password must not exceed ${PASSWORD_RULES.maxLength} characters`,
      );
    }

    if (PASSWORD_RULES.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }

    if (PASSWORD_RULES.requireLowercase && !/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }

    if (PASSWORD_RULES.requireNumber && !/\d/.test(password)) {
      errors.push("Password must contain at least one number");
    }

    if (
      PASSWORD_RULES.requireSpecialChar &&
      !/[!@#$%^&*(),.?":{}|<>]/.test(password)
    ) {
      errors.push("Password must contain at least one special character");
    }

    return errors;
  };

  // Validate individual field
  const validateField = (name, value) => {
    switch (name) {
      case "currentPassword":
        if (!value.trim()) {
          return "Current password is required";
        }
        return "";

      case "newPassword":
        if (!value.trim()) {
          return "New password is required";
        }

        const passwordErrors = validatePassword(value);
        if (passwordErrors.length > 0) {
          return passwordErrors[0]; // Return first error
        }

        // Comment out current password comparison for now
        if (value === formData.currentPassword) {
          return "New password must be different from current password";
        }
        return "";

      case "confirmPassword":
        if (!value.trim()) {
          return "Please confirm your new password";
        }
        if (value !== formData.newPassword) {
          return "Passwords do not match";
        }
        return "";

      default:
        return "";
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Real-time validation for confirm password
    if (name === "newPassword" && formData.confirmPassword) {
      const confirmError =
        formData.confirmPassword !== value ? "Passwords do not match" : "";
      setErrors((prev) => ({
        ...prev,
        confirmPassword: confirmError,
      }));
    }

    if (name === "confirmPassword") {
      const confirmError =
        value !== formData.newPassword ? "Passwords do not match" : "";
      setErrors((prev) => ({
        ...prev,
        confirmPassword: confirmError,
      }));
    }
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  // Toggle password visibility
  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {};

    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setIsSubmitting(true);

    try {
      const { newPassword, currentPassword } = formData;

      const email = user.user.email;
      const response = await handleUpdatePassword(
        newPassword,
        currentPassword,
        email,
      );
      // Reset form after successful password update
      if (response.success) {
        setFormData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        setTouched({});
        setErrors({});
        setShowPasswords({
          currentPassword: false,
          newPassword: false,
          confirmPassword: false,
        });
      }
    } catch (error) {
      console.error("Password change error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // update   enable/disable button
  const isFormValid = useMemo(() => {
    const { newPassword, confirmPassword, currentPassword } = formData;
    return (
      newPassword.length >= 5 &&
      /[A-Z]/.test(newPassword) &&
      /[a-z]/.test(newPassword) &&
      /\d/.test(newPassword) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(newPassword) &&
      newPassword === confirmPassword &&
      newPassword !== "" &&
      currentPassword !== ""
    );
  }, [formData]);

  // Get field error (only show if touched)
  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : "";
  };

  // Check if field has error
  const hasFieldError = (fieldName) => {
    return Boolean(touched[fieldName] && errors[fieldName]);
  };

  return {
    formData,
    errors,
    showPasswords,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    togglePasswordVisibility,
    getFieldError,
    hasFieldError,
    isFormValid,
  };
};
